import React, { useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Plus, CalendarDays } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import { Calendar } from '../../../components/ui/calendar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../../components/ui/dialog';
import { MultiSelect } from '../MultiSelect';
import { CATEGORIES } from '../BriefForm';
import { api, DELIVERABLES, TIERS, LABEL, FIELD, BTN_PRIMARY, errMsg } from './briefApi';

const EMPTY = {
  brand_name: '', objective: '', deliverable: 'Reel', budget_min: '', budget_max: '', niche: [], city: [], state: [],
  followers_min: '', followers_max: '', quality_tier: [], deadline: '', notes: '',
};
const num = (v) => (v === '' ? null : Number(v));

const Field = ({ label, children, className = '' }) => <div className={className}><label className={LABEL}>{label}</label>{children}</div>;

export const CreateBriefDialog = ({ open, onOpenChange, filters, onCreated }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const setList = (k) => (v) => setF((x) => ({ ...x, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.brand_name.trim()) { toast.error('Brand name is required'); return; }
    setBusy(true);
    try {
      const brief = await api.post('', {
        ...f, brand_name: f.brand_name.trim(), deadline: f.deadline || null,
        budget_min: num(f.budget_min), budget_max: num(f.budget_max), followers_min: num(f.followers_min), followers_max: num(f.followers_max),
      });
      toast.success(`Brief created for ${brief.brand_name}`, { description: 'Filters loaded — run suggestions to shortlist creators.' });
      setF(EMPTY);
      onCreated(brief);
    } catch (err) {
      toast.error(errMsg(err, 'Could not create brief'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="create-brief-dialog">
        <DialogHeader>
          <DialogTitle>Create Brief</DialogTitle>
          <DialogDescription>Capture the brand brief. Its filters load into Brief Suggestions so you can shortlist creators.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-4">
          <Field label="Brand Name *"><input value={f.brand_name} onChange={set('brand_name')} className={FIELD} data-testid="create-brief-brand" /></Field>
          <Field label="Deliverable Type">
            <select value={f.deliverable} onChange={set('deliverable')} className={FIELD} data-testid="create-brief-deliverable">
              {DELIVERABLES.map((d) => <option key={d}>{d}</option>)}
            </select>
          </Field>
          <Field label="Campaign Objective" className="col-span-2"><input value={f.objective} onChange={set('objective')} placeholder="e.g. Drive awareness for the monsoon lipstick launch" className={FIELD} data-testid="create-brief-objective" /></Field>
          <Field label="Budget Min (₹)"><input type="number" min={0} value={f.budget_min} onChange={set('budget_min')} className={FIELD} data-testid="create-brief-budget-min" /></Field>
          <Field label="Budget Max (₹)"><input type="number" min={0} value={f.budget_max} onChange={set('budget_max')} className={FIELD} data-testid="create-brief-budget-max" /></Field>
          <Field label="Niche" className="col-span-2"><MultiSelect value={f.niche} onChange={setList('niche')} options={CATEGORIES} clearLabel="Clear all" testid="create-brief-niche" /></Field>
          <Field label="City"><MultiSelect value={f.city} onChange={setList('city')} options={filters.cities} testid="create-brief-city" /></Field>
          <Field label="State"><MultiSelect value={f.state} onChange={setList('state')} options={filters.states} testid="create-brief-state" /></Field>
          <Field label="Followers Min"><input type="number" min={0} value={f.followers_min} onChange={set('followers_min')} className={FIELD} data-testid="create-brief-followers-min" /></Field>
          <Field label="Followers Max"><input type="number" min={0} value={f.followers_max} onChange={set('followers_max')} className={FIELD} data-testid="create-brief-followers-max" /></Field>
          <Field label="Quality Tier"><MultiSelect value={f.quality_tier} onChange={setList('quality_tier')} options={TIERS} testid="create-brief-tier" /></Field>
          <Field label="Deadline">
            <Popover>
              <PopoverTrigger asChild>
                <button type="button" className={`${FIELD} text-left inline-flex items-center gap-2`} data-testid="create-brief-deadline">
                  <CalendarDays size={14} className="text-[#6B6B6B]" />{f.deadline ? format(parseISO(f.deadline), 'd MMM yyyy') : <span className="text-[#A1A1AA]">Pick a date</span>}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start" data-testid="create-brief-deadline-calendar">
                <Calendar mode="single" selected={f.deadline ? parseISO(f.deadline) : undefined} onSelect={(d) => setF((x) => ({ ...x, deadline: d ? format(d, 'yyyy-MM-dd') : '' }))} disabled={{ before: new Date() }} initialFocus />
              </PopoverContent>
            </Popover>
          </Field>
          <Field label="Notes (optional)" className="col-span-2">
            <textarea rows={3} value={f.notes} onChange={set('notes')} className={`${FIELD} h-auto py-2`} data-testid="create-brief-notes" />
          </Field>
          <div className="col-span-2 flex justify-end">
            <button type="submit" disabled={busy} className={BTN_PRIMARY} data-testid="create-brief-submit">
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Create Brief
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
